import { useRef, useState } from 'react';
import { FileText, Plus, Trash2 } from 'lucide-react';
import {
  formatCurrency,
  multiplyMoney,
  sumMoney,
  type PlanningItem,
  type Quote,
} from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { SearchableListSelect } from '../common/SearchableListSelect';
import { useVendors } from '../../hooks/useVendors';
import { useCreateQuote, useRecordQuote } from '../../hooks/useNeeds';
import { useAnalyzeQuote } from '../../hooks/useQuoteImport';
import type { AnalyzeQuoteResponse } from '../../api/quote-import';
import {
  DOCUMENT_FILE_TYPES,
  documentFileProblem,
  uploadAndRead,
} from '../expenses/documentReader';
import { extractApiErrorMessage } from '../../utils/apiError';
import {
  draftFromQuote,
  draftToFigures,
  itemLabel,
  matchLinesToItems,
  newExtra,
  type QuoteDraft,
} from './quoteDraft';

export interface QuoteEntryModalProps {
  open: boolean;
  eventId: string;
  needId: string;
  /** The need's category, to offer its vendors first when adding a quote. */
  vendorCategoryId?: string | null;
  /** The quote whose figures are being entered; null adds one received outside a request. */
  quote: Quote | null;
  /** The need's items, one price field each. */
  items: PlanningItem[];
  onClose: () => void;
}

const INPUT =
  'w-full h-11 sm:h-10 px-3 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500';

/**
 * Enter a vendor's figures — typed, or read from their file by the AI, which
 * proposes which item each of its lines prices for the producer to confirm.
 * Entering figures on a quote that has some keeps the earlier as a revision.
 */
export function QuoteEntryModal(props: QuoteEntryModalProps) {
  const title = props.quote ? `Quote from ${props.quote.vendorName}` : 'Add a quote I received';
  return (
    <EntityModal open={props.open} onClose={props.onClose} title={title} size="lg">
      {props.open && <QuoteEntryForm key={props.quote?.id ?? 'new'} {...props} />}
    </EntityModal>
  );
}

function QuoteEntryForm({
  eventId,
  needId,
  vendorCategoryId,
  quote,
  items,
  onClose,
}: QuoteEntryModalProps) {
  const [draft, setDraft] = useState<QuoteDraft>(() => draftFromQuote(quote));
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [reading, setReading] = useState(false);
  const [readNote, setReadNote] = useState('');
  const fileInput = useRef<HTMLInputElement>(null);
  const { data: vendors = [], isLoading: vendorsLoading } = useVendors(
    { isActive: true },
    { enabled: !quote },
  );
  const create = useCreateQuote(needId);
  const record = useRecordQuote(needId);
  const analyze = useAnalyzeQuote();
  const saving = create.isPending || record.isPending;

  const set = (patch: Partial<QuoteDraft>) => setDraft((d) => ({ ...d, ...patch }));
  const inCategory = (v: (typeof vendors)[number]) =>
    !!vendorCategoryId && v.categories.some((c) => c.vendorCategoryId === vendorCategoryId);
  const vendorOptions = [...vendors].sort((a, b) => Number(inCategory(b)) - Number(inCategory(a)));

  const linesTotal = sumMoney([
    ...items.map((i) => {
      const p = Number(draft.itemPrices[i.id]);
      return draft.itemPrices[i.id] && Number.isFinite(p) ? multiplyMoney(i.quantity || 1, p) : 0;
    }),
    ...draft.extras.map((e) => {
      const p = Number(e.unitPrice);
      const q = Number(e.quantity);
      return Number.isFinite(p) && Number.isFinite(q) ? multiplyMoney(q, p) : 0;
    }),
  ]);

  const read = async (label: string, run: () => Promise<AnalyzeQuoteResponse>) => {
    setError('');
    setReading(true);
    try {
      const { documentId, analysis } = await run();
      const matches = matchLinesToItems(analysis.lineItems, items);
      setDraft((d) => {
        const itemPrices = { ...d.itemPrices };
        const extras = [...d.extras];
        analysis.lineItems.forEach((line, index) => {
          const itemId = matches[index];
          if (itemId) itemPrices[itemId] = String(line.unitPrice);
          else
            extras.push(
              newExtra({
                description: line.description,
                quantity: String(line.quantity),
                unitPrice: String(line.unitPrice),
              }),
            );
        });
        return {
          ...d,
          itemPrices,
          extras,
          total: analysis.totalAmount != null ? String(analysis.totalAmount) : d.total,
          documentIds: d.documentIds.includes(documentId)
            ? d.documentIds
            : [...d.documentIds, documentId],
        };
      });
      const matched = matches.filter(Boolean).length;
      setReadNote(
        `Read ${analysis.lineItems.length} lines from ${label}: ${matched} matched to items, ${
          analysis.lineItems.length - matched
        } as extras. Check them before saving.`,
      );
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not read the file'));
    } finally {
      setReading(false);
    }
  };

  const readFile = (file: File) => {
    const problem = documentFileProblem(file);
    if (problem) return setError(problem);
    return read(file.name, () => uploadAndRead(file, eventId, (r) => analyze.mutateAsync(r)));
  };

  // A file the vendor sent through the quote's link is already on file.
  const readable = (quote?.documents ?? []).filter((d) => DOCUMENT_FILE_TYPES.includes(d.mimeType));

  const save = async () => {
    setError('');
    if (!quote && !vendorId) return setError('Choose the vendor the quote is from.');
    const result = draftToFigures(draft, items);
    if ('error' in result) return setError(result.error);
    try {
      if (quote) await record.mutateAsync({ quoteId: quote.id, body: result.figures });
      else await create.mutateAsync({ vendorId: vendorId!, ...result.figures });
      onClose();
    } catch (err) {
      setError(extractApiErrorMessage(err, 'Could not save the quote'));
    }
  };

  return (
    <div className="space-y-5">
      {error && (
        <div role="alert" className="px-3 py-2 bg-red-50 text-red-700 text-sm rounded">
          {error}
        </div>
      )}

      {!quote && (
        <div>
          <label
            htmlFor="quote-entry-vendor"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Vendor
          </label>
          <SearchableListSelect
            inputId="quote-entry-vendor"
            items={vendorOptions}
            value={vendorId}
            onChange={(v) => setVendorId((v as string) || null)}
            getValue={(v) => v.id}
            getLabel={(v) => v.businessName}
            getSubtitle={(v) => v.categories.map((c) => c.categoryName).join(', ') || null}
            isLoading={vendorsLoading}
            placeholder="Search vendors"
            ariaLabel="Vendor"
          />
        </div>
      )}

      <div className="rounded-xl border border-dashed border-gray-300 p-3 flex flex-col sm:flex-row sm:items-center gap-2">
        <FileText size={18} className="text-gray-400 shrink-0" />
        <p className="text-sm text-gray-600 flex-1">
          {readNote ||
            (readable.length
              ? 'Their file is in. Read the figures from it, or from another file.'
              : 'Have their PDF or a photo of it? Read the figures from the file.')}
        </p>
        <input
          ref={fileInput}
          type="file"
          accept={DOCUMENT_FILE_TYPES.join(',')}
          className="hidden"
          data-testid="quote-entry-file"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = '';
            if (file) void readFile(file);
          }}
        />
        {readable.map((doc) => (
          <button
            key={doc.id}
            type="button"
            onClick={() =>
              void read(doc.fileName, () => analyze.mutateAsync({ eventId, documentId: doc.id }))
            }
            disabled={reading}
            className="min-h-11 sm:min-h-9 px-3 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50 truncate max-w-full"
          >
            Read {doc.fileName}
          </button>
        ))}
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={reading}
          className="min-h-11 sm:min-h-9 px-3 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 disabled:opacity-50"
        >
          {reading ? 'Reading…' : 'Read from a file'}
        </button>
      </div>

      {items.length > 0 && (
        <fieldset>
          <legend className="text-sm font-medium text-gray-700 mb-2">Price per item</legend>
          <div className="space-y-2">
            {items.map((item) => (
              <div key={item.id} className="flex items-center gap-3">
                <label
                  htmlFor={`quote-item-${item.id}`}
                  className="flex-1 text-sm text-gray-700 min-w-0"
                >
                  <span className="text-gray-500">{item.quantity} ×</span> {itemLabel(item)}
                </label>
                <input
                  id={`quote-item-${item.id}`}
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  placeholder="Unit price"
                  value={draft.itemPrices[item.id] ?? ''}
                  onChange={(e) =>
                    set({ itemPrices: { ...draft.itemPrices, [item.id]: e.target.value } })
                  }
                  className={`${INPUT} max-w-[9rem]`}
                />
              </div>
            ))}
          </div>
        </fieldset>
      )}

      <fieldset>
        <legend className="text-sm font-medium text-gray-700 mb-2">Extras</legend>
        <p className="text-xs text-gray-500 mb-2">
          Lines the vendor added that price none of the items.
        </p>
        <div className="space-y-2">
          {draft.extras.map((extra, index) => (
            <div
              key={extra.key}
              className="grid grid-cols-[1fr_4.5rem_7rem_auto] gap-2 items-center"
            >
              <input
                aria-label={`Extra ${index + 1} description`}
                value={extra.description}
                placeholder="Description"
                onChange={(e) =>
                  set({
                    extras: draft.extras.map((x) =>
                      x.key === extra.key ? { ...x, description: e.target.value } : x,
                    ),
                  })
                }
                className={INPUT}
              />
              <input
                aria-label={`Extra ${index + 1} quantity`}
                type="number"
                inputMode="decimal"
                value={extra.quantity}
                onChange={(e) =>
                  set({
                    extras: draft.extras.map((x) =>
                      x.key === extra.key ? { ...x, quantity: e.target.value } : x,
                    ),
                  })
                }
                className={INPUT}
              />
              <input
                aria-label={`Extra ${index + 1} unit price`}
                type="number"
                inputMode="decimal"
                placeholder="Unit price"
                value={extra.unitPrice}
                onChange={(e) =>
                  set({
                    extras: draft.extras.map((x) =>
                      x.key === extra.key ? { ...x, unitPrice: e.target.value } : x,
                    ),
                  })
                }
                className={INPUT}
              />
              <button
                type="button"
                aria-label={`Remove extra ${index + 1}`}
                onClick={() => set({ extras: draft.extras.filter((x) => x.key !== extra.key) })}
                className="min-h-11 min-w-11 sm:min-h-9 sm:min-w-9 flex items-center justify-center text-gray-400 hover:text-red-600"
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
        <button
          type="button"
          onClick={() => set({ extras: [...draft.extras, newExtra()] })}
          className="mt-2 min-h-11 sm:min-h-0 inline-flex items-center gap-1 text-sm text-primary-700 hover:underline"
        >
          <Plus size={14} /> Add an extra
        </button>
      </fieldset>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label
            htmlFor="quote-entry-total"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Total
          </label>
          <input
            id="quote-entry-total"
            type="number"
            inputMode="decimal"
            min={0}
            step="0.01"
            value={draft.total}
            placeholder={linesTotal > 0 ? `${formatCurrency(linesTotal)} from the lines` : ''}
            onChange={(e) => set({ total: e.target.value })}
            className={INPUT}
          />
        </div>
        <div>
          <label
            htmlFor="quote-entry-valid"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            Valid until <span className="text-xs text-gray-400 font-normal">(optional)</span>
          </label>
          <input
            id="quote-entry-valid"
            type="date"
            value={draft.validUntil}
            onChange={(e) => set({ validUntil: e.target.value })}
            className={INPUT}
          />
        </div>
      </div>

      <fieldset className="space-y-2">
        <legend className="text-sm font-medium text-gray-700 mb-1">Deposit</legend>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <SearchableListSelect
            items={[
              { id: 'none', label: 'No deposit' },
              { id: 'percent', label: 'A percent of the total' },
              { id: 'amount', label: 'A fixed amount' },
            ]}
            value={draft.depositKind}
            onChange={(v) =>
              set({ depositKind: ((v as string) || 'none') as QuoteDraft['depositKind'] })
            }
            getValue={(o) => o.id}
            getLabel={(o) => o.label}
            clearable={false}
            ariaLabel="Deposit kind"
          />
          {draft.depositKind !== 'none' && (
            <>
              <input
                aria-label={draft.depositKind === 'percent' ? 'Deposit percent' : 'Deposit amount'}
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                placeholder={draft.depositKind === 'percent' ? '%' : '$'}
                value={draft.depositValue}
                onChange={(e) => set({ depositValue: e.target.value })}
                className={INPUT}
              />
              <SearchableListSelect
                items={[
                  { id: 'signing', label: 'Due on signing' },
                  { id: 'date', label: 'Due on a day' },
                ]}
                value={draft.depositDue}
                onChange={(v) =>
                  set({ depositDue: ((v as string) || 'signing') as QuoteDraft['depositDue'] })
                }
                getValue={(o) => o.id}
                getLabel={(o) => o.label}
                clearable={false}
                ariaLabel="Deposit due"
              />
            </>
          )}
        </div>
        {draft.depositKind !== 'none' && draft.depositDue === 'date' && (
          <input
            aria-label="Deposit due day"
            type="date"
            value={draft.depositDate}
            onChange={(e) => set({ depositDate: e.target.value })}
            className={`${INPUT} sm:max-w-xs`}
          />
        )}
      </fieldset>

      <div>
        <label htmlFor="quote-entry-notes" className="block text-sm font-medium text-gray-700 mb-1">
          Notes
        </label>
        <textarea
          id="quote-entry-notes"
          rows={2}
          value={draft.notes}
          onChange={(e) => set({ notes: e.target.value })}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onClose}
          className="min-h-11 sm:min-h-9 px-4 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving || reading}
          className="min-h-11 sm:min-h-9 px-4 bg-primary-600 text-white text-sm font-semibold rounded-lg hover:bg-primary-700 disabled:opacity-50"
        >
          {saving ? 'Saving…' : 'Save quote'}
        </button>
      </div>
    </div>
  );
}
